import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  projectFoldersQuery,
  type ProjectFolder,
  type ProjectFoldersResponse,
} from '@/entities/project-folder';
import { ROUTES } from '@/shared/config/routes';
import { RAIL_PAGE_SIZE } from '../../components/ApprovalFolderRail';

/**
 * Where the section goes when the open folder is deleted.
 *
 * Answered from the rail's own cache, and answered while the folder is still
 * in it, so the destination never waits on a refetch: the folder below it in
 * the tree, else the one above, else the folder that held it. Only a folder
 * whose siblings were never fetched falls back to the section root, which
 * picks the first folder left or offers to create one.
 */
export function useFolderRouteAfterDelete(projectId: string, folder: ProjectFolder) {
  const queryClient = useQueryClient();
  const parentId = folder.breadcrumbs.at(-1)?.id;

  return useCallback(() => {
    const siblings =
      queryClient.getQueryData<ProjectFoldersResponse>(
        projectFoldersQuery(projectId, { parentId, page: 1, pageSize: RAIL_PAGE_SIZE }).queryKey,
      )?.items ?? [];

    const position = siblings.findIndex((sibling) => sibling.id === folder.id);
    const neighbour =
      position === -1 ? undefined : (siblings[position + 1] ?? siblings[position - 1]);
    const target = neighbour?.id ?? parentId;

    return target
      ? ROUTES.PROJECT_FOLDER_DETAIL(projectId, target)
      : ROUTES.PROJECT_FOLDERS(projectId);
  }, [queryClient, projectId, parentId, folder.id]);
}
